import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  StatusChangeEvent,
  ValidationErrors
} from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { TranslatePipe } from '@ngx-translate/core';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzFormModule } from 'ng-zorro-antd/form';
import { FieldValidationMsgPipe } from '@shared/pipes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

type InlineInputValue = string | number | undefined;

@Component({
  selector: 'moxi-inline-input-control',
  imports: [
    FormsModule,
    TranslatePipe,

    FieldValidationMsgPipe,

    // Ant Design
    NzGridModule,
    NzInputModule,
    NzInputNumberModule,
    NzFormModule
  ],
  templateUrl: './inline-input-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InlineInputControlComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InlineInputControlComponent),
      multi: true
    }
  ]
})
export class InlineInputControlComponent implements ControlValueAccessor {
  private readonly destroyRef = inject(DestroyRef);

  private readonly controlSignal = signal<AbstractControl | null>(null);

  private hasSubscribedToEvents = false;

  label = input<string>();
  inputWidth = input('100%');
  isNumber = input(false);
  step = input<number>(1);
  maxValue = input<number | undefined>(Number.MAX_SAFE_INTEGER);
  minValue = input<number | undefined>(Number.MIN_SAFE_INTEGER);
  cssClass = input<string | undefined>();
  suffix = input<string | undefined>();
  isRequired = input(false);

  valueChange = output<string | undefined>();

  showErrors = signal(false);

  hasErrors = computed(() => this.showErrors() && this.currentErrors !== null);

  value: InlineInputValue = undefined;
  disabled = false;
  touched = false;
  currentErrors: ValidationErrors | null = null;

  get control(): AbstractControl | null {
    return this.controlSignal();
  }

  private set control(value: AbstractControl | null) {
    this.controlSignal.set(value);

    if (value && !this.hasSubscribedToEvents) {
      this.setupControlEventSubscription(value);
    }
  }

  onChange: (value: InlineInputValue) => void = () => undefined;
  onTouched: () => void = () => undefined;

  writeValue(value: InlineInputValue): void {
    this.value = value;
    this.updateErrors();
  }

  registerOnChange(fn: (value: InlineInputValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputTouched(): void {
    this.touched = true;
    this.onTouched();
  }

  onTextChange(event: Event | number): void {
    if (event == null) {
      this.value = undefined;
      this.onChange(undefined);
      this.valueChange.emit(undefined);

      return;
    }

    const text = this.isNumber()
      ? (event as number)
      : ((event as Event).target as HTMLInputElement).value;

    this.value = this.isNumber() ? Number(text) : text;
    this.onChange(this.value);
    this.valueChange.emit(this.value as string);
    this.updateErrors();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const errors = this.calculateErrors(control);
    this.currentErrors = errors;
    this.control = control;

    return errors;
  }

  private calculateErrors(control: AbstractControl): ValidationErrors | null {
    if (this.isRequired() && !control.value && control.value !== 0) {
      return { required: true };
    }

    if (
      this.isNumber() &&
      control.value !== null &&
      control.value !== undefined
    ) {
      const numValue = Number(control.value);

      if (isNaN(numValue)) {
        return { invalidNumber: true };
      }

      const maxValue = this.maxValue();
      if (maxValue !== undefined && numValue > maxValue) {
        return { max: { actual: numValue, max: maxValue } };
      }

      const minValue = this.minValue();
      if (minValue !== undefined && numValue < minValue) {
        return { min: { actual: numValue, min: minValue } };
      }
    }

    return null;
  }

  private updateErrors(): void {
    // Create a mock control to validate current value
    const mockControl = {
      value: this.value
    } as AbstractControl;

    this.currentErrors = this.calculateErrors(mockControl);
  }

  private setupControlEventSubscription(control: AbstractControl): void {
    this.hasSubscribedToEvents = true;

    control.events
      .pipe(
        filter(event => event instanceof StatusChangeEvent),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.updateErrors();

        if (control.invalid && control.dirty) {
          this.showErrors.set(true);
          return;
        }

        this.showErrors.set(false);
      });
  }
}
